import { useEffect, useMemo, useState } from 'react'
import { router, useForm } from '@inertiajs/react'
import { Megaphone } from 'lucide-react'
import CommandCenterLayout from '@/frontend/layouts/CommandCenterLayout'
import Composer from './Composer'
import HistoryCard from './HistoryCard'
import HowItWorksCard from './HowItWorksCard'
import ReachCard from './ReachCard'
import ReviewModal from './ReviewModal'
import type { BroadcastFormData, HistoryEntry, OrganizationOption } from './types'

interface Props {
  organizations: OrganizationOption[]
  history: HistoryEntry[]
}

function BroadcastsIndex({ organizations, history }: Props) {
  const form = useForm<BroadcastFormData>({
    title: '',
    content: '',
    org_scope: 'all',
    organization_ids: [],
    channels: { panel: false, email: false },
  })
  const [reviewOpen, setReviewOpen] = useState(false)

  // With org_scope 'all' the manual selection is ignored: always submit an empty list.
  form.transform((data) => (data.org_scope === 'all' ? { ...data, organization_ids: [] } : data))

  const selectedOrgs = useMemo(
    () =>
      form.data.org_scope === 'all'
        ? organizations
        : organizations.filter((org) => form.data.organization_ids.includes(org.id)),
    [organizations, form.data.org_scope, form.data.organization_ids]
  )

  const channelsOn = form.data.channels.panel || form.data.channels.email
  const reach = useMemo(
    () => ({
      orgs: selectedOrgs.length,
      agents: selectedOrgs.reduce((sum, org) => sum + org.eligibleAgentsCount, 0),
      // Sessões atingidas = sessões de usuário ativas + a sessão principal de
      // cada agente (uma entrega por sessão), espelhando o histórico.
      sessions: selectedOrgs.reduce(
        (sum, org) => sum + org.activeSessionsCount + org.eligibleAgentsCount,
        0
      ),
      // Usuários só entram no alcance quando um canal adicional está ativo.
      users: channelsOn ? selectedOrgs.reduce((sum, org) => sum + org.usersCount, 0) : 0,
    }),
    [selectedOrgs, channelsOn]
  )

  const hasSending = history.some((entry) => entry.status === 'sending')
  useEffect(() => {
    if (!hasSending) return
    const interval = setInterval(() => router.reload({ only: ['history'] }), 4000)
    return () => clearInterval(interval)
  }, [hasSending])

  const updateForm = (patch: Partial<BroadcastFormData>) => {
    form.setData((previous) => ({ ...previous, ...patch }))
  }

  const handleConfirm = () => {
    form.post('/cc/broadcasts', {
      onSuccess: () => {
        form.reset()
        setReviewOpen(false)
      },
    })
  }

  return (
    <div>
      <div className="mb-6 flex items-center gap-3">
        <Megaphone className="h-6 w-6 text-red-500" />
        <div>
          <h2 className="text-2xl font-bold text-foreground">Comunicados</h2>
          <p className="text-sm text-muted-foreground">
            Envie avisos de plataforma para agentes — novas funções, mudanças gerais, manutenção
          </p>
        </div>
      </div>

      <div className="grid grid-cols-1 items-start gap-6 lg:grid-cols-[minmax(0,1fr)_360px]">
        <Composer
          data={form.data}
          organizations={organizations}
          reach={reach}
          onUpdate={updateForm}
          onReview={() => setReviewOpen(true)}
        />
        <div className="space-y-5">
          <ReachCard reach={reach} channels={form.data.channels} />
          <HowItWorksCard />
          <HistoryCard history={history} />
        </div>
      </div>

      <ReviewModal
        open={reviewOpen}
        data={form.data}
        selectedOrgs={selectedOrgs}
        reach={reach}
        processing={form.processing}
        onClose={() => setReviewOpen(false)}
        onConfirm={handleConfirm}
      />
    </div>
  )
}

BroadcastsIndex.layout = (page: React.ReactElement) => <CommandCenterLayout>{page}</CommandCenterLayout>

export default BroadcastsIndex
